import React from "react";
import { Workflow } from "../../../services/WorkflowService";
import { Application } from "../../../model_gen/application";
import { Button } from "../../ui/button";
import { Input } from "../../ui/input";
import { Label } from "../../ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../ui/select";
import { SaveIcon, Loader2Icon, TrashIcon, PlusIcon } from "lucide-react";

interface WorkflowConfigTabProps {
  workflow: Workflow;
  settingsName: string;
  setSettingsName: (val: string) => void;
  settingsDescription: string;
  setSettingsDescription: (val: string) => void;
  settingsTimeout: number;
  setSettingsTimeout: (val: number) => void;
  settingsApplicationId: number | undefined;
  setSettingsApplicationId: (val: number | undefined) => void;
  applications: Application[];
  savingSettings: boolean;
  onSave: () => void;
  onDelete: () => void;
  onNewApplication: () => void;
}

const WorkflowConfigTab: React.FC<WorkflowConfigTabProps> = ({
  workflow,
  settingsName,
  setSettingsName,
  settingsDescription,
  setSettingsDescription,
  settingsTimeout,
  setSettingsTimeout,
  settingsApplicationId,
  setSettingsApplicationId,
  applications,
  savingSettings,
  onSave,
  onDelete,
  onNewApplication,
}) => {
  return (
    <div className="space-y-6">
      <div className="space-y-4">
        <div className="space-y-2">
          <Label htmlFor="wf-name">Workflow Name</Label>
          <Input
            id="wf-name"
            value={settingsName}
            onChange={(e) => setSettingsName(e.target.value)}
          />
        </div>
        <div className="space-y-2">
          <Label htmlFor="wf-desc">Description</Label>
          <Input
            id="wf-desc"
            value={settingsDescription}
            onChange={(e) => setSettingsDescription(e.target.value)}
            placeholder="Optional description"
          />
        </div>
        <div className="grid grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label>Application</Label>
            <Select
              value={settingsApplicationId?.toString() || ""}
              onValueChange={(val) => {
                if (val === "new_application") {
                  onNewApplication();
                } else {
                  setSettingsApplicationId(val ? Number(val) : undefined);
                }
              }}
            >
              <SelectTrigger>
                <SelectValue placeholder="Select application (optional)" />
              </SelectTrigger>
              <SelectContent>
                {applications
                  .filter((app) => app.id != null && !isNaN(app.id))
                  .map((app) => (
                    <SelectItem key={app.id} value={app.id!.toString()}>
                      {app.application_name}
                    </SelectItem>
                  ))}
                <SelectItem value="new_application" className="border-t border-border mt-1 pt-2">
                  <div className="flex items-center gap-2 text-maitai-green font-medium">
                    <PlusIcon className="w-4 h-4" />
                    Create New Application
                  </div>
                </SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-2">
            <Label htmlFor="wf-timeout">Timeout (seconds)</Label>
            <Input
              id="wf-timeout"
              type="number"
              min={1}
              max={300}
              value={settingsTimeout}
              onChange={(e) =>
              setSettingsTimeout(
                Math.min(300, Math.max(1, Number(e.target.value) || 30))
              )
            }
            />
          </div>
        </div>
        <div className="space-y-2">
          <Label>Ref Name</Label>
          <div className="text-sm text-muted-foreground">
            <code className="bg-muted px-2 py-0.5 rounded font-mono text-xs">
              {workflow.workflow_ref_name}
            </code>
            <span className="ml-2 text-xs">(cannot be changed after creation)</span>
          </div>
        </div>
      </div>

      <div className="flex items-center justify-between pt-4 border-t border-border">
        <Button
          variant="destructive"
          size="sm"
          onClick={onDelete}
          className="gap-1"
        >
          <TrashIcon className="w-4 h-4" />
          Delete Workflow
        </Button>
        <Button
          onClick={onSave}
          disabled={savingSettings || !settingsName.trim()}
          className="gap-2"
        >
          {savingSettings ? (
            <Loader2Icon className="w-4 h-4 animate-spin" />
          ) : (
            <SaveIcon className="w-4 h-4" />
          )}
          Save Configuration
        </Button>
      </div>
    </div>
  );
};

export default WorkflowConfigTab;
